// @flow

import React, { PureComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import moment from 'moment';
import styled from 'styled-components';
import type { RouterHistory } from 'react-router';
import { i18n, getCompanyCurrency } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import PostJournalEntryMutation from 'main-app/mutations/PostJournalEntry';
import VoidJournalEntryMutation from 'main-app/mutations/VoidJournalEntry';
import Table from 'shared/components/common/Table';

const Wrapper = styled.div`
  padding: 0 24px;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ status }) => {
    switch (status) {
      case 'DRAFT': return '#FFF3E0';
      case 'POSTED': return '#E8F5E9';
      case 'VOIDED': return '#FFEBEE';
      default: return '#F5F5F5';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'DRAFT': return '#E65100';
      case 'POSTED': return '#2E7D32';
      case 'VOIDED': return '#C62828';
      default: return '#616161';
    }
  }};
`;

const SourceBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #E3F2FD;
  color: #1565C0;
`;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: getCompanyCurrency(),
  }).format(value);

type Props = {
  history: RouterHistory,
  journalEntryEdges: Array<Object>,
  onViewDetail: (entry: Object) => void,
};

class JournalEntriesTable extends PureComponent<Props> {
  handlePost = (entry: Object) => {
    PostJournalEntryMutation.commit({
      variables: { input: { id: entry.id } },
      onCompleted: () => {
        Actions.alertNotification(
          i18n.t('Journal entry posted successfully.'),
          'success',
        );
      },
      onError: () => {
        Actions.alertNotification(
          i18n.t('Error posting journal entry.'),
          'error',
        );
      },
    });
  };

  handleVoid = (entry: Object) => {
    VoidJournalEntryMutation.commit({
      variables: { input: { id: entry.id } },
      onCompleted: () => {
        Actions.alertNotification(
          i18n.t('Journal entry voided successfully.'),
          'success',
        );
      },
      onError: () => {
        Actions.alertNotification(
          i18n.t('Error voiding journal entry.'),
          'error',
        );
      },
    });
  };

  getColumns = () => [
    {
      Header: i18n.t('Entry #'),
      accessor: 'node.entryNumber',
      width: 120,
    },
    {
      Header: i18n.t('Date'),
      accessor: 'node.entryDate',
      width: 120,
      Cell: ({ value }: *) =>
        value ? moment(value).format('MM/DD/YYYY') : '—',
    },
    {
      Header: i18n.t('Memo'),
      accessor: 'node.memo',
      minWidth: 200,
      Cell: ({ value }: *) => value || '—',
    },
    {
      Header: i18n.t('Source'),
      accessor: 'node.sourceType',
      width: 140,
      Cell: ({ value }: *) => (
        <SourceBadge>{value.replace(/_/g, ' ')}</SourceBadge>
      ),
    },
    {
      Header: i18n.t('Status'),
      accessor: 'node.status',
      width: 110,
      Cell: ({ value }: *) => <StatusPill status={value}>{value}</StatusPill>,
    },
    {
      Header: i18n.t('Debit'),
      accessor: 'node.totalDebit',
      width: 130,
      Cell: ({ value }: *) => formatCurrency(value),
    },
    {
      Header: i18n.t('Credit'),
      accessor: 'node.totalCredit',
      width: 130,
      Cell: ({ value }: *) => formatCurrency(value),
    },
    {
      Header: i18n.t('Created By'),
      accessor: 'node.createdByUser',
      width: 140,
      Cell: ({ value }: *) =>
        value ? `${value.firstName} ${value.lastName}` : i18n.t('System'),
    },
    {
      Header: i18n.t('Created'),
      accessor: 'node.createdAt',
      width: 120,
      Cell: ({ value }: *) => moment(value).format('MM/DD/YYYY'),
    },
  ];

  getContextMenuItems = () => [
    {
      label: i18n.t('View Details'),
      onClick: (row: Object) => this.props.onViewDetail(row.node),
    },
    {
      label: i18n.t('Post'),
      onClick: (row: Object) => this.handlePost(row.node),
      condition: (row: Object) => row.node.status === 'DRAFT',
    },
    {
      label: i18n.t('Void'),
      onClick: (row: Object) => this.handleVoid(row.node),
      condition: (row: Object) => row.node.status === 'POSTED',
    },
  ];

  render() {
    const { journalEntryEdges, onViewDetail } = this.props;

    if (!journalEntryEdges || journalEntryEdges.length === 0) {
      return null;
    }

    return (
      <Wrapper>
        <Table
          data={journalEntryEdges}
          columns={this.getColumns()}
          onRowClick={(rowData: Object) => onViewDetail(rowData.node)}
          contextMenuItems={this.getContextMenuItems()}
        />
      </Wrapper>
    );
  }
}

export default createFragmentContainer(JournalEntriesTable, {
  journalEntryEdges: graphql`
    fragment JournalEntriesTable_journalEntryEdges on JournalEntryEdge
      @relay(plural: true) {
      node {
        id
        entryNumber
        entryDate
        memo
        sourceType
        status
        syncStatus
        postedAt
        voidedAt
        totalDebit
        totalCredit
        createdByUser {
          id
          firstName
          lastName
        }
        createdAt
      }
    }
  `,
});
